import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, InputAdornment } from '@mui/material';
import { CalculationsSummaryProps } from '../types/ESALTypes';
import { ESALFormulas } from '../ESALFormulas';
import DecimalTextField from '../../../../components/DecimalTextField';
import { resolveDecimalFieldPolicy } from '../../../../utils/decimalInput';

/**
 * Excel-format traffic analysis summary component for ESAL modal
 * Matches the exact structure from TRAFFIC LOAD.txt (H84-H91) with 4 editable inputs and 4 calculated outputs
 * Now uses Redux state directly without local state duplication
 */
const CalculationsSummary: React.FC<CalculationsSummaryProps> = ({ summaryData, totalESAL, isCalculating, onSummaryInputsChange, onValidityChange, resetKey }) => {
    const { t } = useTranslation();

    // Use summaryData directly from Redux (no local state) - memoized to prevent re-renders
    const editableInputs = useMemo(
        () => ({
            dd: summaryData.directionalDistribution,
            dl: summaryData.laneDistribution,
            analysisPeriod: summaryData.analysisPeriod,
            growthRate: summaryData.yearGrowthRate,
        }),
        [summaryData.directionalDistribution, summaryData.laneDistribution, summaryData.analysisPeriod, summaryData.yearGrowthRate]
    );

    // Calculate derived values based on inputs (Excel-exact formulas from H84-H91)
    const calculatedValues = useMemo(() => {
        // H84 = Q82*365 (Total passages per year)
        const totalPassagesPerYear = totalESAL * 365;

        // H89 = TGF = [ (1 + r)^Y – 1] / ln (1 + r)
        const tgf = ESALFormulas.calculateTrafficGrowthFactor(editableInputs.growthRate, editableInputs.analysisPeriod);

        // H90 = w'18 = TGF * N1 (using totalPassagesPerYear as N1)
        const totalVolumeTraffic = tgf * totalPassagesPerYear;

        // H91 = w18 = DD * DL * w'18
        const trafficDesignLane = editableInputs.dd * editableInputs.dl * totalVolumeTraffic;

        return {
            totalPassagesPerYear,
            tgf,
            totalVolumeTraffic,
            trafficDesignLane,
        };
    }, [editableInputs, totalESAL]);

    /**
     * Handle input field changes - dispatch directly to Redux
     */
    const handleInputChange = useCallback(
        (field: keyof typeof editableInputs, value: number) => {
            if (onSummaryInputsChange) {
                const updatedInputs = {
                    dd: editableInputs.dd,
                    dl: editableInputs.dl,
                    analysisPeriod: editableInputs.analysisPeriod,
                    growthRate: editableInputs.growthRate,
                    trafficDesignLane: calculatedValues.trafficDesignLane,
                    [field]: value,
                };
                onSummaryInputsChange(updatedInputs);
            }
        },
        [editableInputs, calculatedValues.trafficDesignLane, onSummaryInputsChange]
    );

    /**
     * Render editable input field
     */
    const renderEditableField = (field: keyof typeof editableInputs, label: string, unit: string, decimals = 2) => (
        <TableRow>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50', width: '60%' }}>{label}</TableCell>
            <TableCell sx={{ textAlign: 'right', minWidth: 120, width: '40%' }}>
                <DecimalTextField
                    size="small"
                    value={editableInputs[field]}
                    policy={resolveDecimalFieldPolicy({ decimalPlaces: decimals }, 'esalSummary')}
                    emptyPolicy="publishZero"
                    fieldLabel={label}
                    onValueChange={value => handleInputChange(field, value ?? 0)}
                    onValidityChange={isValid => onValidityChange?.(`summary:${field}`, isValid)}
                    resetKey={resetKey}
                    inputProps={{ style: { textAlign: 'right' } }}
                    sx={{ width: '100%' }}
                    InputProps={{
                        endAdornment: unit && <InputAdornment position="end">{unit}</InputAdornment>,
                    }}
                />
            </TableCell>
        </TableRow>
    );

    /**
     * Render calculated field (read-only)
     */
    const renderCalculatedField = (label: string, value: number, unit: string, decimals = 2) => (
        <TableRow>
            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.50', width: '60%' }}>{label}</TableCell>
            <TableCell sx={{ textAlign: 'right', fontFamily: 'monospace', width: '40%' }}>
                {ESALFormulas.formatNumber(value, decimals)} {unit}
            </TableCell>
        </TableRow>
    );

    return (
        <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Loading indicator */}
            {isCalculating && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                        {t('esal_calculating', 'Calculating...')}
                    </Typography>
                </Box>
            )}

            {/* Excel-format summary table - exact match to H84-H91 */}
            <Box sx={{ mb: 3, flex: 1 }}>
                <TableContainer component={Paper} sx={{ maxHeight: '400px' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', width: '60%' }}>{t('esal_table_description', 'Description')}</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.main', color: 'white', textAlign: 'center', width: '40%' }}>{t('esal_table_value', 'Value')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* INPUT FIELDS (4 editable - matching Excel H85-H88) */}
                            {renderEditableField('dd', t('esal_summary_directional_distribution', 'Directional distribution factor'), '', 2)}
                            {renderEditableField('dl', t('esal_summary_lane_distribution', 'Lane distribution factor'), '', 2)}
                            {renderEditableField('analysisPeriod', t('esal_summary_analysis_period', 'Analysis period'), '', 0)}
                            {renderEditableField('growthRate', t('esal_summary_growth_rate', 'Year growth rate'), '%', 2)}

                            {/* CALCULATED FIELDS (4 read-only - matching Excel H84, H89-H91) */}
                            {renderCalculatedField(
                                t('esal_summary_total_passages_per_year', 'Total passages per year'),
                                calculatedValues.totalPassagesPerYear,
                                t('esal_unit_passages_per_year', 'passages/year'),
                                0
                            )}
                            {renderCalculatedField(t('esal_summary_traffic_growth_factor', 'Traffic Growth Factor'), calculatedValues.tgf, '', 2)}
                            {renderCalculatedField(t('esal_summary_total_volume_traffic', 'Total volume of traffic'), calculatedValues.totalVolumeTraffic, t('esal_unit_passages', 'passages'), 0)}
                            {renderCalculatedField(t('esal_summary_traffic_design_lane', 'Traffic in design lane'), calculatedValues.trafficDesignLane, t('esal_unit_passages', 'passages'), 0)}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
};

export default CalculationsSummary;
